import apiClient from '../utils/apiClient';
import { apiUtils } from '../utils/commonUtils';

const isApiSuccess = (data) => {
  if (!data) return false;
  if (typeof data.status === 'string' && data.status.toLowerCase() === 'success') return true;
  if (data.success === true) return true;
  return false;
};

// Fetch packages
export const fetchPackages = async (filters = {}) => {
  try {
    const {
      search = '',
      per_page = 15
    } = filters;

    const params = {};
    
    if (search) params.search = search;
    if (per_page) params.per_page = per_page;

    const response = await apiClient.get('/tenant-packages', { params });


    if (response.data && response.data.data) {
      return {
        success: true,
        data: response.data.data,
        meta: {
          current_page: response.data.current_page,
          per_page: response.data.per_page,
          total: response.data.total,
          last_page: response.data.last_page,
          from: response.data.from,
          to: response.data.to,
          first_page_url: response.data.first_page_url,
          last_page_url: response.data.last_page_url,
          next_page_url: response.data.next_page_url,
          prev_page_url: response.data.prev_page_url,
          links: response.data.links
        },
        message: 'Packages fetched successfully'
      };
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to fetch packages');
  }
};

export const createPackage = async (packageData) => {
  try {
    const response = await apiClient.post('/tenant-packages', {
      name: packageData.name,
      harga: packageData.price,
      storage_gb: packageData.storage_gb,
      package_detail: packageData.benefits
    });

    if (isApiSuccess(response.data) || (response.status >= 200 && response.status < 300)) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Package created successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to create package');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to create package');
  }
};

// Update package
export const updatePackage = async (packageId, packageData) => {
  try {
    const response = await apiClient.put(`/tenant-packages/${packageId}`, {
      name: packageData.name,
      harga: packageData.price,
      storage_gb: packageData.storage_gb,
      package_detail: packageData.benefits
    });

    if (isApiSuccess(response.data) || (response.status >= 200 && response.status < 300)) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Package updated successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to update package');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to update package');
  }
};

// Delete package
export const deletePackage = async (packageId) => {
  try {
    const response = await apiClient.delete(`/tenant-packages/${packageId}`);

    if (isApiSuccess(response.data) || (response.status >= 200 && response.status < 300)) {
      return {
        success: true,
        message: response.data.message || 'Package deleted successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to delete package');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to delete package');
  }
};

export const transformPackageData = (apiPackage) => {
  return {
    id: apiPackage.id,
    name: apiPackage.name,
    price: parseFloat(apiPackage.harga) || 0,
    storage_gb: parseInt(apiPackage.storage_gb) || 0,
    benefits: Array.isArray(apiPackage.details)
      ? apiPackage.details.map((d) => d?.detail).filter(Boolean)
      : (apiPackage.package_detail || []),
    status: apiPackage.status || 'Active',
    created_at: apiPackage.created_at,
    updated_at: apiPackage.updated_at,
    deleted_at: apiPackage.deleted_at
  };
};

// Transform multiple packages
export const transformPackagesData = (apiPackages) => {
  return apiPackages.map(transformPackageData);
};

// Toggle Active/Inactive status
export const togglePackageStatus = async (packageId) => {
  try {
    const response = await apiClient.post(`/tenant-packages/${packageId}/toggle-status`);
    if (isApiSuccess(response.data) || (response.status >= 200 && response.status < 300)) {
      return {
        success: true,
        data: response.data.data ? transformPackageData(response.data.data) : null,
        message: response.data.message || 'Status updated',
      };
    }
    throw new Error(response.data.message || 'Failed to toggle status');
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to toggle status');
  }
};

// Check tenants subscribed to a package
export const getPackageSubscribers = async (packageId) => {
  try {
    const response = await apiClient.get(`/tenant-packages/${packageId}/subscribers`);
    return {
      success: true,
      count: response.data.count,
      data: response.data.data,
    };
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to get subscribers');
  }
};

// Migrate all subscribers from one package to another
export const migratePackageSubscribers = async (fromPackageId, toPackageId) => {
  try {
    const response = await apiClient.post(`/tenant-packages/${fromPackageId}/migrate-subscribers`, {
      to_package_id: toPackageId,
    });
    if (isApiSuccess(response.data) || (response.status >= 200 && response.status < 300)) {
      return {
        success: true,
        message: response.data.message || 'Tenants migrated',
        count: response.data.count,
      };
    }
    throw new Error(response.data.message || 'Failed to migrate');
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to migrate subscribers');
  }
};

// Get package by id (with details array)
export const getPackageById = async (packageId) => {
  try {
    const response = await apiClient.get(`/tenant-packages/${packageId}`);
    const data = response.data;
    if (data && (isApiSuccess(data) || response.status === 200)) {
      const pkg = data.data || data;
      return {
        success: true,
        data: transformPackageData(pkg),
        message: data.message || 'Package fetched successfully'
      };
    }
    throw new Error(data?.message || 'Failed to get package');
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to get package');
  }
};
